import { useCallback, useMemo } from 'react';
import { type CartDisplay, CartItemDisplay } from '@/api/registration/cart';
import { useGroupCartQuery } from '@/registration/hook/useGroupCartQuery';
import { TempRegistration, TicketData } from '@/api/registration';
import { TicketType } from '@/api/constant';

export interface CartTempRegistration extends TempRegistration {
  registrationTypeId: number | null, // This tells the registration type if (A.K.A. main ticket type, or  delegate type id) of the temp registration.
  tickets: CartItemDisplay[],
}

function getTicketUniqueKey(ticketId: number, ticketType: TicketType): string{
  return `${ticketType}-${ticketId}`;
}

export function useGroupShoppingCart(eventId: string) {
  const {data, isLoading, isError} = useGroupCartQuery(eventId);

  // Get all temp registrations from the shopping cart.
  const cartTempRegistrationList = useMemo(() => {
    const _list: CartTempRegistration[] = [];
    if (data) {
      const cartItems = (data as CartDisplay).cartItems;
      if (cartItems && Array.isArray(cartItems)) {
        for (const item of cartItems) {
          if (item.productId) {
            const registrationTypeId = item.ticketType === TicketType.Main ? item.productId : null;
            if (item.tempRegistrations && Array.isArray(item.tempRegistrations)) {
              for (const rego of item.tempRegistrations) {
                const _rego = {...rego};
                const _existingTempRegistrationIndex = _list.findIndex((tempRego) => { return tempRego.id === _rego.id});
                if (_existingTempRegistrationIndex !== -1) {
                  // The temp registration is already in the list, add the ticket id, and update the registration type id if needed.
                  if (registrationTypeId !== null) {
                    _list[_existingTempRegistrationIndex].registrationTypeId = registrationTypeId;
                  }
                  _list[_existingTempRegistrationIndex].tickets.push(item);
                } else {
                  // Add the temp registration to the list.
                  _list.push({..._rego, registrationTypeId, tickets: [item]});
                }
              }
            }
          }
        }
      }
    }
    return _list;
  }, [
    data,
  ]);

  // Get all the temp registrations that have been added to the ticket.
  const getTicketTempRegistrationList = useCallback((ticket: TicketData): CartTempRegistration[] => {
    const _list: CartTempRegistration[] = [];
    if (cartTempRegistrationList.length > 0) {
      const _ticketUniqueKey = getTicketUniqueKey(ticket.id, ticket.type);
      const _addedTempRegos: number[] = [];
      for (const regoItem of cartTempRegistrationList) {
        for (const cartTicket of regoItem.tickets) {
          if (getTicketUniqueKey(cartTicket.productId, cartTicket.ticketType) === _ticketUniqueKey
            && !_addedTempRegos.includes(regoItem.id)
          ) {
            _addedTempRegos.push(regoItem.id);
            _list.push(regoItem);
          }
        }
      }
    }
    return _list;
  }, [
    cartTempRegistrationList,
  ]);

  const getTicketCartItemId = useCallback((ticketId: number): number | null => {
    const cartItems = (data as CartDisplay).cartItems;
    for (const item of cartItems) {
      if (item.productId === ticketId) {
        return item.id;
      }
    }
    return null;
  }, [
    data,
  ]);

  // return result;
  return {
    isLoading,
    cartTempRegistrationList,
    getTicketCartItemId,
    getTicketTempRegistrationList,
  };
}
